/// <amd-module name="Environments/Client/EnvironmentDeploymentHistoryService" />
import { TaskResult, TaskOrchestrationOwner, EnvironmentDeploymentExecutionRecord } from "DistributedTask/Client/Contracts/TaskAgent";
import { getTaskAgentClient, ITaskAgentRestClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { ServiceInstanceIds, DataProviderIds } from "Environments/Client/Constants";
import { IEnvironmentDeploymentHistoryResult, IEnvironmentDeploymentHistoryViewRecord, IEnvironmentPageData } from "Environments/Client/EnvironmentContracts";
import { PageContextHelper } from "Environments/Client/PageContextHelper";
import { EnvironmentStageDeploymentData } from "Environments/RemoteClients/Contracts/Traceability";
import { IEnvironmentsRestClient, getEnvironmentsClient } from "Environments/Client/RestClient/Environments";

import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IJobsResult {
   executionRecord: IEnvironmentDeploymentHistoryViewRecord;
}

export interface IEnvironmentDeploymentHistoryService extends IVssService {
    getEnvironmentDeploymentExecutionRecords(
        environmentId: number,
        resourceId?: number,
        pipelineDefinitionIdFilter?: number,
        statusFilter?: TaskResult,
        continuationToken?: string,
        top?: number): IEnvironmentDeploymentHistoryResult | undefined;

    getEnvironmentDeploymentExecutionRecordsAsync(
        environmentId: number,
        resourceId?: number,
        pipelineDefinitionIdFilter?: number,
        statusFilter?: TaskResult,
        continuationToken?: number,
        top?: number): Promise<IEnvironmentDeploymentHistoryResult | undefined>;

    getEnvironmentDeploymentExecutionRecordsRestApi(
        environmentId: number,
        continuationToken?: number,
        top?: number): Promise<EnvironmentDeploymentExecutionRecord[]>;

    getEnvironmentStageJobs(environmentId: number, continuationToken?: number): EnvironmentStageDeploymentData | undefined;

    getEnvironmentStageJobsAsync(environmentId: number, continuationToken?: number): Promise<EnvironmentStageDeploymentData | undefined>;
    
    getLinkedPipelineDefinitions(environmentId: number): Promise<TaskOrchestrationOwner[] | undefined>;
}

class EnvironmentDeploymentHistoryService extends VssService implements IEnvironmentDeploymentHistoryService 
{
    public getEnvironmentDeploymentExecutionRecords(
        environmentId: number,
        resourceId?: number,
        pipelineDefinitionIdFilter?: number,
        statusFilter?: TaskResult,
        continuationToken?: string,
        top: number = 10): IEnvironmentDeploymentHistoryResult | undefined 
    {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data: IEnvironmentDeploymentHistoryResult | undefined = contributionService.getData<IEnvironmentDeploymentHistoryResult>(DataProviderIds.EnvironmentDeploymentHistoryDataProvider);
        
        return data;
    }

    public getEnvironmentDeploymentExecutionRecordsAsync(
        environmentId: number,
        resourceId?: number,
        pipelineDefinitionIdFilter?: number,
        statusFilter?: TaskResult,
        continuationToken?: number,
        top: number = 25,
        ignoreCache: boolean = true): Promise<IEnvironmentDeploymentHistoryResult | undefined> 
    {
        let additionalProperties = {
            id: environmentId,
            resourceId: resourceId,
            definitionId: pipelineDefinitionIdFilter,
            status: statusFilter,
            continuationToken: continuationToken,
            top: top
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<IEnvironmentDeploymentHistoryResult>(
            DataProviderIds.EnvironmentDeploymentHistoryDataProvider,
            additionalProperties,
            ignoreCache,
            ServiceInstanceIds.Tfs);
    }

    public getEnvironmentDeploymentExecutionRecordsRestApi(
        environmentId: number,
        continuationToken?: number,
        top: number = 25): Promise<EnvironmentDeploymentExecutionRecord[]>
    {
        const continuationTokenString = continuationToken ? continuationToken.toString() : "";
        return this._getClient().getEnvironmentDeploymentExecutionRecords(this._getProjectName(), environmentId, continuationTokenString, top);
    }

    public getEnvironmentStageJobs(
        environmentId: number,
        continuationToken?: number): EnvironmentStageDeploymentData | undefined
    {
        let additionalProperties = {
            id: environmentId,
            continuationToken: continuationToken,
            top: 1,
            view: "jobshistory"
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        let data = contributionService.getData<EnvironmentStageDeploymentData>(DataProviderIds.EnvironmentRunJobsDataProvider, additionalProperties);

        if (!data) {
            data = contributionService.getData<EnvironmentStageDeploymentData>(DataProviderIds.EnvironmentRunJobsDataProvider);
        }

        if (data && data.jobs) {
            return data;
        }

        return undefined;
    }

    public getEnvironmentStageJobsAsync(
        environmentId: number,
        continuationToken?: number): Promise<EnvironmentStageDeploymentData | undefined> 
    {
        let additionalProperties = {
            id: environmentId,
            continuationToken: continuationToken,
            top: 1,
            view: "jobshistory"
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<EnvironmentStageDeploymentData>(
            DataProviderIds.EnvironmentRunJobsDataProvider,
            additionalProperties,
            false,
            ServiceInstanceIds.Tfs);
    }

    public getLinkedPipelineDefinitions(environmentId: number): Promise<TaskOrchestrationOwner[] | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return Promise.resolve([]);

        // TODO: Un-comment while enabling pipeline definition filter
        // return contributionService.getDataAsync<TaskOrchestrationOwner[]>(
        //     DataProviderIds.LinkedPipelineDefinitionsDataProvider,
        //     { environmentId: environmentId },
        //     true,
        //     ServiceInstanceIds.Tfs);
    }

    private _getClient() {
        return getTaskAgentClient(this.pageContext);
    }

    private _getProjectName(): string {
        const projectName = PageContextHelper.getProjectName(this.pageContext);
        if (!!projectName) {
            return projectName;
        }

        throw new Error("Release rest client :: Project is not available in page data. Cannot make this network call without project information");
    }
}

Services.add("IEnvironmentDeploymentHistoryService", { serviceFactory: EnvironmentDeploymentHistoryService });